import React, { useContext, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useInView, animate } from 'framer-motion';
import { ArrowRight, X, Eye, EyeOff } from 'lucide-react';
import { DataContext } from '@/context';
import { useNavigate } from 'react-router-dom';
import { scrollToId } from '@/utils';

const Counter: React.FC<{ to: number; duration?: number; delay?: number }> = ({ to, duration = 2, delay = 0.5 }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-20px" });

  useEffect(() => {
    const node = ref.current;
    if (!node || !inView) return;

    // Minimalist tech animation: Fast start, slow settle
    const controls = animate(0, to, {
      duration,
      delay,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (value) => {
        node.textContent = Math.floor(value).toString();
      }
    });

    return () => controls.stop();
  }, [to, duration, delay, inView]);

  return <span ref={ref}>0</span>;
};

const Hero: React.FC<{ childRef, openLogin }> = ({ childRef, openLogin }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  const navigate = useNavigate();
  const {
    userInfo,
    setContextValue
  } = useContext(DataContext);


  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let w = canvas.width = window.innerWidth;
    let h = canvas.height = window.innerHeight;

    // Light sources that move along the edges
    // They will oscillate slightly and move along the perimeter
    const lights = [
      { x: w * 0.2, y: 0, r: 500, color: 'rgba(29, 78, 216, 0.15)', vx: 0.5, vy: 0.1, baseR: 500 }, // Top Blue
      { x: w * 0.8, y: h, r: 600, color: 'rgba(14, 165, 233, 0.1)', vx: -0.5, vy: -0.1, baseR: 600 }, // Bottom Cyan
      { x: 0, y: h * 0.4, r: 450, color: 'rgba(88, 28, 135, 0.12)', vx: 0.1, vy: 0.4, baseR: 450 },   // Left Purple
      { x: w, y: h * 0.6, r: 500, color: 'rgba(37, 99, 235, 0.12)', vx: -0.1, vy: -0.4, baseR: 500 }, // Right Blue
    ];

    const resize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      // Re-position lights relatively on resize
      lights[0].x = w * 0.2; lights[0].y = 0;
      lights[1].x = w * 0.8; lights[1].y = h;
      lights[2].x = 0; lights[2].y = h * 0.4;
      lights[3].x = w; lights[3].y = h * 0.6;
    };
    window.addEventListener('resize', resize);

    let time = 0;

    const render = () => {
      ctx.clearRect(0, 0, w, h);
      time += 0.005;

      // Use lighter blending for glow effect
      ctx.globalCompositeOperation = 'screen';

      lights.forEach((light, i) => {
        // Simple organic movement
        light.x += light.vx + Math.sin(time + i) * 0.2;
        light.y += light.vy + Math.cos(time + i * 0.8) * 0.2;

        // Breathing radius
        const currentR = light.baseR + Math.sin(time * 2 + i) * 50;

        // Constrain to edges (loosely)
        if (light.x < -100) light.vx = Math.abs(light.vx);
        if (light.x > w + 100) light.vx = -Math.abs(light.vx);
        if (light.y < -100) light.vy = Math.abs(light.vy);
        if (light.y > h + 100) light.vy = -Math.abs(light.vy);

        // Draw Glow
        const gradient = ctx.createRadialGradient(light.x, light.y, 0, light.x, light.y, currentR);
        gradient.addColorStop(0, light.color);
        gradient.addColorStop(1, 'rgba(0,0,0,0)');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(light.x, light.y, currentR, 0, Math.PI * 2);
        ctx.fill();
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col justify-center items-center pt-20 pb-12 px-4 overflow-hidden bg-black">

      {/* Subtle Tech Grid Background */}
      <div
        className="absolute inset-0 z-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
          `,
          backgroundSize: '100px 100px'
        }}
      />

      {/* Canvas Layer for Dynamic Light */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none z-0"
      />

      {/* Soft Vignette to keep focus center */}
      <div className="absolute inset-0 pointer-events-none z-0 bg-[radial-gradient(circle_at_center,transparent_20%,rgba(0,0,0,0.6)_100%)]" />

      <div className="max-w-6xl mx-auto text-center z-10 space-y-8 md:space-y-12 flex flex-col items-center">

        {/* Main Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="space-y-4"
        >
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-[200] tracking-tight text-white leading-tight mt-20">
            森宇宙好绘<span className="hidden md:inline font-[400] text-blue-500/80"> | </span>
            <br className="block md:hidden" />
            <span className="block md:inline mt-2 md:mt-0">AI设计师0佣金接单平台</span>
          </h1>
          <p className="text-lg md:text-4xl text-gray-200 font-light leading-relaxed tracking-wide !mt-[52px]">
            专为AI设计师打造的商单平台
          </p>
        </motion.div>

        {/* Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-4xl !mt-[20px]" // 这里tailwind会默认注入部分样式,需要添加!强制设置mt为20px
        >
          <p className="text-lg md:text-4xl text-gray-200 font-light leading-relaxed tracking-wide">
            开启副业人生，技能轻松变现
          </p>
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="pt-4 md:pt-8"
        >
          <button
            onClick={() => {
              // if (!userInfo?.user_id) {
              //   childRef?.current?.openLogin?.()
              //   return
              // }
              // navigate('/price');

              // setIsModalOpen(true)
              scrollToId({ id: 'tutorial' })
            }}
            className="group relative px-6 py-2.5 md:px-14 md:py-5 rounded-full text-sm md:text-xl font-medium transition-all duration-300 hover:scale-105 border border-white/10 bg-white/5 backdrop-blur-md shadow-[0_0_30px_rgba(37,99,235,0.1)] hover:shadow-[0_0_50px_rgba(37,99,235,0.25)]"
          >
            {/* Inner highlight */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-50" />

            <span className="relative z-10 flex items-center gap-2 md:gap-3 text-white drop-shadow-md">
              查看接单流程
              <ArrowRight className="w-3.5 h-3.5 md:w-5 md:h-5 transition-transform group-hover:translate-x-1" />
            </span>
          </button>
        </motion.div>

        {/* Core Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-5xl mt-12 md:mt-24"
        >
          <div className="grid grid-cols-4 gap-0 md:gap-12 border-t border-white/10 pt-8 md:pt-12 w-full">
            <div className="text-center px-0.5 md:px-1">
              <div className="text-base md:text-5xl font-bold text-gray-100 mb-1 md:mb-3 font-mono flex items-center justify-center gap-0.5 md:gap-1">
                <Counter to={3000} duration={2.5} />+
              </div>
              <div className="text-[10px] md:text-sm text-gray-500 font-normal tracking-tight md:tracking-widest whitespace-nowrap scale-100 origin-center">每月商单量级</div>
            </div>
            <div className="text-center px-0.5 md:px-1">
              <div className="text-base md:text-5xl font-bold text-gray-100 mb-1 md:mb-3 font-mono flex items-center justify-center gap-0.5 md:gap-1">
                <Counter to={800} duration={2.5} />
                <span className="text-[10px] md:text-2xl font-sans relative top-0 md:top-1">元</span>
              </div>
              <div className="text-[10px] md:text-sm text-gray-500 font-normal tracking-tight md:tracking-widest whitespace-nowrap scale-100 origin-center">平均每单费用</div>
            </div>
            <div className="text-center px-0.5 md:px-1">
              <div className="text-base md:text-5xl font-bold text-gray-100 mb-1 md:mb-3 font-mono flex items-center justify-center gap-0.5 md:gap-1">
                <Counter to={0} duration={2.5} />
                <span className="text-[10px] md:text-2xl font-sans relative top-0 md:top-1">抽成</span>
              </div>
              <div className="text-[10px] md:text-sm text-gray-500 font-normal tracking-tight md:tracking-widest whitespace-nowrap scale-100 origin-center">真正免佣金</div>
            </div>
            <div className="text-center px-0.5 md:px-1">
              <div className="text-base md:text-5xl font-bold text-gray-100 mb-1 md:mb-3 font-mono flex items-center justify-center gap-0.5 md:gap-1">
                <Counter to={1} duration={2.5} />
                <span className="text-[10px] md:text-2xl font-sans relative top-0 md:top-1">秒</span>
              </div>
              <div className="text-[10px] md:text-sm text-gray-500 font-normal tracking-tight md:tracking-widest whitespace-nowrap scale-100 origin-center">设计费秒到账</div>
            </div>
          </div>
        </motion.div>

      </div>

      {/* Login/Register Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="absolute inset-0 bg-black/80 backdrop-blur-md"
            />

            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-[#111] border border-white/10 rounded-3xl shadow-2xl overflow-hidden"
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10"
              >
                <X size={24} />
              </button>

              <div className="p-8 pb-6">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-semibold text-white mb-2">CeMeta</h2>
                  <p className="text-xs text-gray-500 tracking-[0.3em]">森 宇 宙 好 绘 A I</p>
                </div>

                {/* Tabs */}
                <div className="flex justify-center gap-8 mb-8 border-b border-white/10 relative">
                  <button
                    onClick={() => setLoginMethod('email')}
                    className={`pb-3 text-lg font-medium transition-colors relative ${loginMethod === 'email' ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`}
                  >
                    邮箱登录
                    {loginMethod === 'email' && <motion.div layoutId="tab-underline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />}
                  </button>
                  <div className="w-[1px] h-6 bg-white/10 self-center mb-3" />
                  <button
                    onClick={() => setLoginMethod('phone')}
                    className={`pb-3 text-lg font-medium transition-colors relative ${loginMethod === 'phone' ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`}
                  >
                    手机号登录
                    {loginMethod === 'phone' && <motion.div layoutId="tab-underline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />}
                  </button>
                </div>

                {/* Form */}
                <div className="space-y-4">
                  <div className="space-y-4">
                    <input
                      type={loginMethod === 'email' ? "email" : "tel"}
                      placeholder={loginMethod === 'email' ? "请输入邮箱" : "请输入手机号"}
                      className="w-full h-12 px-4 rounded-lg bg-white/5 text-white border border-white/10 focus:bg-white/10 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all outline-none placeholder:text-gray-600"
                    />
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="请输入密码"
                        className="w-full h-12 px-4 rounded-lg bg-white/5 text-white border border-white/10 focus:bg-white/10 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all outline-none placeholder:text-gray-600"
                      />
                      <button
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button className="text-sm text-gray-500 hover:text-gray-300">忘记密码?</button>
                  </div>

                  <button className="w-full h-12 bg-blue-600 hover:bg-blue-500 text-white font-medium text-lg rounded-lg transition-colors shadow-lg shadow-blue-900/20 mt-4">
                    登 录
                  </button>

                  <div className="flex items-center gap-2 mt-4">
                    <input type="checkbox" id="agree" className="w-4 h-4 rounded border-gray-600 bg-white/5 text-blue-600 focus:ring-blue-500 focus:ring-offset-0" />
                    <label htmlFor="agree" className="text-sm text-gray-500">
                      已阅读并同意 <span className="text-gray-300">用户协议</span> 与 <span className="text-gray-300">隐私政策</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="py-6 bg-white/5 text-center text-sm text-gray-500 border-t border-white/10">
                还没有账号? <button className="text-blue-400 font-medium hover:underline">去注册</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Hero;